const chunkId = (tokens) => tokens.map((token) => token.id).join('+');

const cleanToken = (value) => String(value || '')
  .replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, '')
  .toLowerCase();

export const mergeReorderTokens = (tokens = [], target = '', maxChunks = 10) => {
  const limit = Math.max(1, Number(maxChunks) || 10);
  const targetCore = String(target || '').toLowerCase();
  const protectedFlags = tokens.map((token) => cleanToken(token) === targetCore);
  const blocks = tokens.map((token) => [token]);
  const punctuation = new Set([
    ',', '.', ';', ':', '!', '?', '，', '。', '；', '：', '！', '？', '…', '—',
    '(', ')', '[', ']', '{', '}', '"', "'", '`',
  ]);
  const blockLength = (block) => block.reduce(
    (total, token) => total + cleanToken(token).length,
    0,
  );
  const scorePair = (left, right) => {
    let score = blockLength(left) + blockLength(right);
    if (
      punctuation.has(String(left[left.length - 1] || '').slice(-1))
      || punctuation.has(String(right[0] || '')[0])
    ) {
      score += 1000;
    }
    return score;
  };

  while (blocks.length > limit) {
    let tokenOffset = 0;
    const protectedBlocks = blocks.map((block) => {
      const isProtected = block.some((_, index) => protectedFlags[tokenOffset + index]);
      tokenOffset += block.length;
      return isProtected;
    });
    let bestIndex = -1;
    let bestScore = Infinity;
    for (let index = 0; index < blocks.length - 1; index += 1) {
      if (protectedBlocks[index] || protectedBlocks[index + 1]) continue;
      const score = scorePair(blocks[index], blocks[index + 1]);
      if (score < bestScore) {
        bestIndex = index;
        bestScore = score;
      }
    }
    if (bestIndex < 0) break;
    blocks.splice(bestIndex, 2, [...blocks[bestIndex], ...blocks[bestIndex + 1]]);
  }
  return blocks.map((block) => block.join('_'));
};

export const createReorderChunks = (scrambled = []) => ({
  answer: [],
  bank: scrambled.map((word, index) => {
    const token = { id: `token-${index}`, word };
    return { id: chunkId([token]), tokens: [token] };
  }),
});

export const flattenReorderChunks = (chunks = []) => (
  chunks.flatMap((chunk) => chunk.tokens).map((token) => token.word)
);

export const moveReorderChunk = (
  state,
  sourceZone,
  sourceIndex,
  targetZone,
  targetIndex,
) => {
  if (!['answer', 'bank'].includes(sourceZone) || !['answer', 'bank'].includes(targetZone)) {
    return state;
  }
  if (!Number.isInteger(sourceIndex) || sourceIndex < 0) return state;

  const source = [...state[sourceZone]];
  const target = sourceZone === targetZone ? source : [...state[targetZone]];
  const [chunk] = source.splice(sourceIndex, 1);
  if (!chunk) return state;

  let insertionIndex = Number(targetIndex);
  if (!Number.isFinite(insertionIndex)) insertionIndex = target.length;
  if (sourceZone === targetZone && sourceIndex < insertionIndex) {
    insertionIndex -= 1;
  }
  insertionIndex = Math.max(0, Math.min(insertionIndex, target.length));
  target.splice(insertionIndex, 0, chunk);

  if (sourceZone === targetZone) {
    return { ...state, [sourceZone]: target };
  }
  return {
    ...state,
    [sourceZone]: source,
    [targetZone]: target,
  };
};

export const joinReorderChunks = (state, zone, leftIndex) => {
  const chunks = [...(state[zone] || [])];
  const left = chunks[leftIndex];
  const right = chunks[leftIndex + 1];
  if (!left || !right) return state;

  const tokens = [...left.tokens, ...right.tokens];
  chunks.splice(leftIndex, 2, { id: chunkId(tokens), tokens });
  return { ...state, [zone]: chunks };
};

export const splitReorderChunk = (state, zone, chunkIndex, tokenBoundary) => {
  const chunks = [...(state[zone] || [])];
  const chunk = chunks[chunkIndex];
  if (!chunk || tokenBoundary <= 0 || tokenBoundary >= chunk.tokens.length) return state;

  const leftTokens = chunk.tokens.slice(0, tokenBoundary);
  const rightTokens = chunk.tokens.slice(tokenBoundary);
  chunks.splice(
    chunkIndex,
    1,
    { id: chunkId(leftTokens), tokens: leftTokens },
    { id: chunkId(rightTokens), tokens: rightTokens },
  );
  return { ...state, [zone]: chunks };
};
